/**
 * Tags on an arrangement's keys: who or what each key is for, so a leader
 * picking "G" or "Eb" sees at once which is the men's key and which the
 * women's. Free text, kept short and tidy so the same tag reads the same
 * everywhere.
 */

/** What the tag picker offers before anything is typed. */
export const KEY_TAG_SUGGESTIONS = ['Male', 'Female', 'Low', 'High', 'Original'] as const

export const MAX_KEY_TAGS = 4
export const MAX_KEY_TAG_LENGTH = 20

/**
 * Tidies tags as typed: trimmed, inner spaces collapsed, first letter
 * capitalised (a suggestion's own spelling wins: "male" → "Male"), empty and
 * repeated tags (ignoring case) dropped, at most `MAX_KEY_TAGS` of at most
 * `MAX_KEY_TAG_LENGTH` characters.
 */
export function normaliseKeyTags(tags: readonly string[]): string[] {
  const out: string[] = []
  for (const raw of tags) {
    const text = raw.trim().replace(/\s+/g, ' ').slice(0, MAX_KEY_TAG_LENGTH).trim()
    if (!text) continue
    const suggested = KEY_TAG_SUGGESTIONS.find((s) => s.toLowerCase() === text.toLowerCase())
    const tag = suggested ?? text.charAt(0).toUpperCase() + text.slice(1)
    if (out.some((t) => t.toLowerCase() === tag.toLowerCase())) continue
    out.push(tag)
    if (out.length === MAX_KEY_TAGS) break
  }
  return out
}

/** Suggestions not yet on the key, for the picker's quick-add chips. */
export const keyTagSuggestions = (current: readonly string[]): string[] =>
  KEY_TAG_SUGGESTIONS.filter((s) => !current.some((t) => t.toLowerCase() === s.toLowerCase()))

/** "G · Male, Low": a tagged key as one line of text (labels, PDFs, titles). */
export const taggedKeyLabel = (key: { key: string; tags: readonly string[] }): string =>
  key.tags.length > 0 ? `${key.key} · ${key.tags.join(', ')}` : key.key

/**
 * The tags for a key on an arrangement: the arrangement's matching key's tags
 * (none when the key is not one of its keys). How a plan item's chosen key
 * gets its "Male"/"Female" chip.
 */
export function tagsForKey(
  keys: readonly { key: string; tags: readonly string[] }[],
  key: string | null,
): readonly string[] {
  if (!key) return []
  return keys.find((k) => k.key === key)?.tags ?? []
}

export type KeyTagTone = 'teal' | 'pink' | 'violet' | 'neutral'

/**
 * A tag's colour, the same everywhere so a glance tells two keys apart: the
 * common pairs get their own (Male / Female, Low / High), anything else a
 * steady one picked from its letters.
 */
export function keyTagTone(tag: string): KeyTagTone {
  const known: Record<string, KeyTagTone> = {
    male: 'teal',
    female: 'pink',
    low: 'violet',
    high: 'violet',
    original: 'neutral',
  }
  const fixed = known[tag.toLowerCase()]
  if (fixed) return fixed
  const tones: KeyTagTone[] = ['teal', 'pink', 'violet']
  let sum = 0
  for (const char of tag.toLowerCase()) sum += char.charCodeAt(0)
  return tones[sum % tones.length] ?? 'neutral'
}
